import { useEffect, useRef, useState, useCallback } from 'react';
import debounce from 'lodash/debounce';

function useAnamAudioActivity(
  videoRef,
  { threshold = 10, silenceDebounceMs = 2000 } = {}
) {
  const [isAvatarTalking, setIsAvatarTalking] = useState(false);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);
  const cleanupRef = useRef(null);
  const isTalkingRef = useRef(false);

  const startAnalyzing = useCallback(
    (stream) => {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      // Every reconnect (token refresh, avatar shown again) delivers a new
      // stream through 'canplay'. Tear down the previous analyser first, or its
      // animation frame loop and AudioContext are leaked and left running.
      cleanupRef.current?.();
      cleanupRef.current = null;

      const audioContext = new AudioCtx();
      audioContextRef.current = audioContext;

      let silenceDebounceScheduled = false;

      const debouncedSetFalse = debounce(() => {
        silenceDebounceScheduled = false;
        isTalkingRef.current = false;
        setIsAvatarTalking(false);
      }, silenceDebounceMs);

      const cancelSilenceDebounce = () => {
        debouncedSetFalse.cancel();
        silenceDebounceScheduled = false;
      };

      const scheduleSetFalse = () => {
        if (silenceDebounceScheduled) return;
        silenceDebounceScheduled = true;
        debouncedSetFalse();
      };

      audioContext.resume().then(() => {
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyserRef.current = analyser;

        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const checkActivity = () => {
          analyser.getByteFrequencyData(dataArray);
          const average =
            dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
          const hasAudio = average > threshold;

          if (hasAudio) {
            cancelSilenceDebounce();
            if (!isTalkingRef.current) {
              isTalkingRef.current = true;
              setIsAvatarTalking(true);
            }
          } else if (isTalkingRef.current) {
            scheduleSetFalse();
          }

          animFrameRef.current = requestAnimationFrame(checkActivity);
        };

        checkActivity();

        cleanupRef.current = () => {
          cancelAnimationFrame(animFrameRef.current);
          cancelSilenceDebounce();
          source.disconnect();
          analyser.disconnect();
          audioContext.close();
        };
      });
    },
    [threshold, silenceDebounceMs]
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleStreamReady = () => {
      const stream = video.srcObject;
      if (!stream || !(stream instanceof MediaStream)) return;
      if (stream.getAudioTracks().length === 0) return;
      startAnalyzing(stream);
    };

    if (video.srcObject) {
      handleStreamReady();
    } else {
      video.addEventListener('canplay', handleStreamReady);
    }

    return () => {
      video.removeEventListener('canplay', handleStreamReady);
      cleanupRef.current?.();
    };
  }, [videoRef, startAnalyzing]);

  return isAvatarTalking;
}

export default useAnamAudioActivity;
